import { NavigationService } from '@omnia/ui/src/lib/services/navigation.service'
import { observable, runInAction } from 'mobx'
import type { Location } from 'react-router-dom-v7'
import type { AllPagesKeys, PagesKeys, PagesType, SubPageKeys, SubPagesType } from '@/shared/models/pages'
import { PagesEnum } from '@/shared/models/pages'
import { cmsDataService } from '@/shared/services/cms.data.service'
import { urlService } from '@/shared/services/url.service'
import { Pages } from '@/web/pages/pages'

class KinepolisNavigationService extends NavigationService<PagesEnum, SubPagesType> {
    protected getOverrideSiteIdPages(): Array<keyof PagesEnum> {
        return []
    }

    protected getPages(): PagesType {
        return Pages as unknown as PagesType
    }

    public constructor() {
        super(new PagesEnum())

        if (import.meta.hot) {
            const hotData = import.meta.hot.data
            if (!hotData?.navigationService) {
                return
            }

            const hotPageObs = hotData.navigationService.pageObs.get()
            const hotIsBurgerMenuOpen = hotData.navigationService.isBurgerMenuOpen.get()

            this.pageObs.set(hotPageObs)
            this.isBurgerMenuOpen.set(hotIsBurgerMenuOpen)

            for (const pageKey of Object.keys(hotData.navigationService.Pages)) {
                const hotPage = hotData.navigationService.Pages[pageKey]
                const coldPage = Pages[pageKey]
                coldPage.urls = hotPage.urls

                if (coldPage.subPages) {
                    for (const coldSubPage of coldPage.subPages) {
                        const hotSubPage = hotPage.subPages?.find(sp => sp.alias === coldSubPage.alias)
                        if (!hotSubPage) continue
                        coldSubPage.urls = hotSubPage.urls
                    }
                }
            }
        }
    }

    public pageObs = observable.box<keyof PagesEnum>(undefined)

    public isBurgerMenuOpen = observable.box<boolean>(false)

    public setBurgerMenuOpen(isOpen: boolean) {
        runInAction(() => {
            this.isBurgerMenuOpen.set(isOpen)
        })
    }

    public isCurrentPage(pageKey: keyof PagesEnum): boolean {
        return this.getCurrentPage() === pageKey
    }

    public getCurrentPage: () => keyof PagesEnum | undefined = () => {
        return this.pageObs.get()
    }

    public get PagesKeys() {
        return new PagesEnum()
    }

    public goToPageOrSubPage(page: keyof PagesEnum, subPage?: SubPageKeys, params?: { [key: string]: string } | null) {
        const url = this.getPageUrl(page, params, false, subPage)
        this.goTo(url)
    }

    public getPageOrSubpageUrl(page: PagesKeys, subPage?: SubPageKeys, params?: { [key: string]: string } | null) {
        return this.getPageUrl(page, params, false, subPage)
    }

    public getCurrentSubPage(page: keyof PagesEnum, location: Location) {
        return navigationService.Pages[page]?.subPages?.find(subPage => {
            if (!subPage.urls?.length) return false // no urls means no node in the cms

            let url = navigationService.getPageUrl(page, undefined, false, subPage.alias)
            if (subPage.params?.length) {
                url = url.replace(`:${subPage.params[0]}/`, '')
            }
            return location.pathname.includes(url)
        })
    }

    /**
     * This method returns a URL for custom subpage names where one type of subpage can exist multiple times.
     * For example, the cinema URL can have one or more subpages where the subpage is a name of a cinema:
     * i.e., `cinema/cinema-1`, `cinema/cinema-2`.
     *
     * @param page The main page type from PagesEnum
     * @param subPageName The name of the subpage (e.g., a specific cinema name)
     * @param params Optional query parameters to append to the URL
     * @returns The complete URL for the custom subpage
     *
     * @note This assumes we have 1 level nesting of sub-page
     */
    public getNamedSubPageUrl = (page: keyof PagesEnum, subPageName: string, params: Record<string, string> = {}) => {
        // `getPageUrl` returns a first subpage url, hence a logic to replace a subpage
        const pageName = `/${subPageName}`.toLowerCase()
        const url = this.getPageUrl(page).replace(/\/[^/]*\/?$/, pageName)
        return url + this.getQueryParams(params)
    }

    public goToNamedSubpage(page: keyof PagesEnum, subPage: string, params?: { [key: string]: string } | null) {
        const url = this.getNamedSubPageUrl(page, subPage, params ?? {})
        this.goTo(url)
    }

    /**
     * Check if the page or subpage is configured in the CMS
     * */
    public pageExist(pageName: AllPagesKeys, subpageName?: SubPageKeys) {
        const page = this.getPages()[pageName]
        if (!subpageName) return !!page.urls?.length

        const subpage = page.subPages?.find(subPage => subPage?.alias === subpageName)
        return !!subpage?.urls?.length
    }

    public navigateSelectedLanguage(languageCode: string) {
        const culture = cmsDataService.get().culture

        if (languageCode && languageCode.toLowerCase() !== culture.toLowerCase()) {
            const versionUrl = urlService.getVersion(languageCode)

            // eslint-disable-next-line no-restricted-properties -- ok for language redirects
            const searchParams = window.location.search

            if (versionUrl) {
                // eslint-disable-next-line no-restricted-properties -- ok for language redirects
                document.location.assign(versionUrl + searchParams)
            } else {
                // Navigate home
                const home = urlService.getHomeUrlVersion(languageCode)
                if (home) {
                    // eslint-disable-next-line no-restricted-properties -- ok for language redirects
                    document.location.assign(home + searchParams)
                } else {
                    this.goToHome()
                }
            }
        }
    }
}

export const navigationService = new KinepolisNavigationService()

if (import.meta.hot) {
    import.meta.hot.on('vite:beforeUpdate', () => {
        import.meta.hot.data.navigationService = navigationService
    })
}
